import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { fmtMoney, fmtPctPlain } from "@/lib/format";
import { ArrowLeft, Sparkle, TrendUp, TrendDown, Warning, Rocket, Megaphone } from "@phosphor-icons/react";
import { streamAIInsights } from "@/lib/api";

const bold = (s) => s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
const renderBullets = (text) => {
    const lines = text.split("\n");
    const sections = {}; let current = null;
    for (const l of lines) {
        const t = l.trim();
        if (t.startsWith("### ")) { current = t.slice(4); sections[current] = []; }
        else if ((t.startsWith("- ") || t.startsWith("* ")) && current) sections[current].push(t.slice(2));
    }
    return sections;
};

const ANNOUNCEMENTS = [
    { date: "12 Feb 2026", category: "Investment", headline: "New strategic investment approved", desc: "Group Executive Committee approved a $180M growth investment in the renewables division targeting 400 MW additional capacity by 2027." },
    { date: "28 Jan 2026", category: "Partnership", headline: "Major commercial partnership signed", desc: "Long-term distribution partnership signed with a global consumer-electronics OEM, expected to add $65M annual revenue from H2 2026." },
    { date: "18 Jan 2026", category: "Transformation", headline: "Reporting automation milestone achieved", desc: "Month-end close cycle reduced from 12 to 7 working days across the top 40 entities under the Reporting Automation programme." },
];

const INITIATIVES = [
    { name: "Working Capital Optimisation", status: "On Track", expected: "$25M", realised: "$14M", pct: 56, tone: "success" },
    { name: "Procurement Efficiency", status: "At Risk", expected: "$12M", realised: "$4M", pct: 33, tone: "warn" },
    { name: "Reporting Automation", status: "On Track", expected: "30% effort reduction", realised: "18%", pct: 60, tone: "success" },
    { name: "Commercial Productivity", status: "Delayed", expected: "$18M", realised: "$6M", pct: 33, tone: "danger" },
];

const TONE_CLS = { success: "text-fp-success bg-fp-success/10", warn: "text-fp-saffron bg-fp-saffron/10", danger: "text-fp-terracotta bg-fp-terracotta/10" };

export default function ExecutivePack() {
    const [data, setData] = useState(null);
    const [narrative, setNarrative] = useState("");

    useEffect(() => {
        client.get("/dashboard/overview").then((r) => setData(r.data));
        const ctrl = new AbortController();
        streamAIInsights("group", null,
            (d) => setNarrative((t) => t + d),
            () => {}, () => {}, ctrl.signal);
        return () => ctrl.abort();
    }, []);

    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;
    const s = data.snapshot;
    const sections = renderBullets(narrative);

    const sectors = [...data.sector_breakdown].sort((a, b) => b.yoy_growth_pct - a.yoy_growth_pct);
    const top = sectors[0], watch = sectors[Math.floor(sectors.length / 2)], under = sectors[sectors.length - 1];
    const activeInitiatives = INITIATIVES.length;
    const totalTarget = "$55M + 30% reduction";
    const onTrackPct = Math.round(INITIATIVES.filter((i) => i.status === "On Track").length / INITIATIVES.length * 100);

    return (
        <div>
            <PageHeader
                overline={<Link to="/" className="hover:text-fp-ink inline-flex items-center gap-1"><ArrowLeft size={11} /> Executive Overview</Link>}
                title="Executive Performance Pack"
                description="One-page executive summary reusing consolidated performance data, narrative and initiatives across the group."
                period={s.period_label}
            >
                <button
                    data-testid="pdf-download-btn"
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-2 bg-fp-ink text-white px-4 py-2 font-sans text-xs hover:bg-fp-ink-hover transition-colors no-print"
                    title="Save this pack as a PDF via your browser print dialog"
                >
                    <FilePdf size={12} weight="fill" /> Download PDF
                </button>
            </PageHeader>
            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-8">

                {/* A. Executive Summary bullets */}
                <div className="bg-fp-panel border-l-4 border-fp-ink p-8" data-testid="pack-summary">
                    <div className="flex items-center gap-2 mb-4"><Sparkle size={14} weight="fill" /><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-secondary">A · Executive Summary</div></div>
                    {Object.keys(sections).length === 0 && <div className="font-sans text-sm text-fp-secondary">Preparing narrative…</div>}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {Object.entries(sections).map(([title, items]) => (
                            <div key={title}>
                                <div className="font-serif text-lg text-fp-ink mb-2">{title}</div>
                                <ul className="space-y-1.5">
                                    {items.map((b, i) => <li key={i} className="font-sans text-sm text-fp-secondary leading-snug pl-3 border-l border-fp-border" dangerouslySetInnerHTML={{ __html: bold(b) }} />)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* B. Headline Performance */}
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">B · Headline Performance</div>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        <KpiCard label="Revenue" value={fmtMoney(s.revenue.value_musd, { decimals: 0 })} subValue={`vs Budget: ${fmtPctPlain(s.revenue.budget_variance_pct)}`} delta={s.revenue.yoy_pct} deltaLabel="YoY" trend={s.revenue.trend} variant="primary" />
                        <KpiCard label="EBITDA" value={fmtMoney(s.ebitda.value_musd, { decimals: 0 })} subValue={`Margin: ${fmtPctPlain(s.ebitda.margin_pct)}`} delta={s.ebitda.mom_pct} deltaLabel="MoM" trend={s.ebitda.trend} variant="success" />
                        <KpiCard label="Net Income" value={fmtMoney(s.net_income.value_musd, { decimals: 0 })} trend={s.net_income.trend} variant="teal" />
                        <KpiCard label="Free Cash Flow" value={fmtMoney(s.free_cash_flow.value_musd, { decimals: 0 })} trend={s.free_cash_flow.trend} variant="plum" />
                        <KpiCard label="ROIC" value={fmtPctPlain(s.roic_pct)} subValue={`ROE: ${fmtPctPlain(s.roe_pct)}`} variant="warn" />
                    </div>
                </div>

                {/* C. Business Performance */}
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">C · Business Performance</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <BusinessCard label="Top Performer" name={top.sector_name} metric={`${fmtPctPlain(top.yoy_growth_pct)} YoY · ${fmtPctPlain(top.ebitda_margin_pct)} margin`} tone="success" Icon={TrendUp} />
                        <BusinessCard label="Watchlist" name={watch.sector_name} metric={`${fmtPctPlain(watch.yoy_growth_pct)} YoY · ${fmtPctPlain(watch.ebitda_margin_pct)} margin`} tone="warn" Icon={Warning} />
                        <BusinessCard label="Underperformer" name={under.sector_name} metric={`${fmtPctPlain(under.yoy_growth_pct)} YoY · ${fmtPctPlain(under.ebitda_margin_pct)} margin`} tone="danger" Icon={TrendDown} link={`/sector/${under.sector_id}?watchlist=1`} />
                    </div>
                </div>

                {/* D. Risks & Attention (bullets from narrative) */}
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">D · Key Risks & Management Attention</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white border border-fp-border p-6">
                            <div className="font-serif text-lg text-fp-ink mb-2">Key Risks</div>
                            <ul className="space-y-1.5">{(sections["Key Risks / Watch-outs"] || []).map((b, i) => <li key={i} className="font-sans text-sm text-fp-secondary pl-3 border-l border-fp-terracotta" dangerouslySetInnerHTML={{ __html: bold(b) }} />)}</ul>
                        </div>
                        <div className="bg-white border border-fp-border p-6">
                            <div className="font-serif text-lg text-fp-ink mb-2">Management Attention</div>
                            <ul className="space-y-1.5">{(sections["Management Attention"] || []).map((b, i) => <li key={i} className="font-sans text-sm text-fp-secondary pl-3 border-l border-fp-saffron" dangerouslySetInnerHTML={{ __html: bold(b) }} />)}</ul>
                        </div>
                    </div>
                </div>

                {/* E. Transformation Portfolio */}
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">E · Transformation Portfolio</div>
                    <div className="bg-white border border-fp-border p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 pb-6 border-b border-fp-border">
                            <div><div className="font-mono text-2xl text-fp-ink">{activeInitiatives}</div><div className="font-sans text-[10px] uppercase tracking-widest text-fp-muted mt-1">Active Initiatives</div></div>
                            <div><div className="font-mono text-2xl text-fp-ink">{totalTarget}</div><div className="font-sans text-[10px] uppercase tracking-widest text-fp-muted mt-1">Target Value</div></div>
                            <div><div className="font-mono text-2xl text-fp-success">{onTrackPct}%</div><div className="font-sans text-[10px] uppercase tracking-widest text-fp-muted mt-1">On Track</div></div>
                        </div>
                        <div className="space-y-3">
                            {INITIATIVES.map((i) => <InitiativeRow key={i.name} i={i} />)}
                        </div>
                    </div>
                </div>

                {/* F. Outlook */}
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">F · Outlook</div>
                    <div className="bg-white border border-fp-border p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div><div className="font-sans text-xs text-fp-muted uppercase tracking-widest mb-1">FY Revenue Trajectory</div><div className="font-mono text-2xl text-fp-ink">{fmtMoney(s.revenue.value_musd * 12, { decimals: 0 })}</div><div className="font-mono text-xs text-fp-secondary">annualised run-rate</div></div>
                            <div><div className="font-sans text-xs text-fp-muted uppercase tracking-widest mb-1">Budget Achievement</div><div className={`font-mono text-2xl ${s.revenue.budget_variance_pct >= 0 ? "text-fp-success" : "text-fp-terracotta"}`}>{fmtPctPlain(s.revenue.budget_variance_pct)}</div><div className="font-mono text-xs text-fp-secondary">latest month</div></div>
                            <div><div className="font-sans text-xs text-fp-muted uppercase tracking-widest mb-1">FCF Coverage</div><div className="font-mono text-2xl text-fp-ink">{fmtMoney(s.free_cash_flow.value_musd, { decimals: 0 })}</div><div className="font-mono text-xs text-fp-secondary">monthly generation</div></div>
                        </div>
                    </div>
                </div>

                {/* G. Announcements */}
                <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">G · Key Announcements</div>
                    <div className="space-y-3">
                        {ANNOUNCEMENTS.map((a, i) => (
                            <div key={i} className="bg-white border border-fp-border p-5 flex items-start gap-4">
                                <Megaphone size={16} className="text-fp-plum shrink-0 mt-1" weight="fill" />
                                <div className="flex-1">
                                    <div className="flex items-baseline gap-3 mb-1"><span className="font-mono text-[10px] uppercase tracking-widest text-fp-muted">{a.date}</span><span className="px-1.5 py-0.5 bg-fp-panel font-mono text-[9px] uppercase tracking-widest text-fp-ink">{a.category}</span></div>
                                    <div className="font-serif text-lg text-fp-ink">{a.headline}</div>
                                    <div className="font-sans text-sm text-fp-secondary mt-1">{a.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="pt-6"><Link to="/" data-testid="back-to-overview" className="inline-flex items-center gap-2 bg-fp-ink text-white px-6 py-2.5 font-sans text-sm hover:bg-fp-ink-hover transition-colors"><ArrowLeft size={14} /> Back to Executive Overview</Link></div>
            </div>
        </div>
    );
}

const BusinessCard = ({ label, name, metric, tone, Icon, link }) => {
    const cls = TONE_CLS[tone];
    const inner = (
        <div className={`p-5 border-l-4 ${tone === "success" ? "border-fp-success" : tone === "warn" ? "border-fp-saffron" : "border-fp-terracotta"} bg-white border border-fp-border hover:shadow-sm transition-shadow`}>
            <div className="flex items-center justify-between mb-2"><div className="font-sans text-[10px] uppercase tracking-[0.25em] text-fp-muted">{label}</div><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-sans text-[10px] tracking-wider uppercase ${cls}`}><Icon size={11} weight="fill" />{tone === "success" ? "Strong" : tone === "warn" ? "Watch" : "Weak"}</span></div>
            <div className="font-serif text-xl text-fp-ink">{name}</div>
            <div className="font-mono text-xs text-fp-secondary mt-1">{metric}</div>
        </div>
    );
    return link ? <Link to={link} data-testid="business-underperformer-link">{inner}</Link> : inner;
};

const InitiativeRow = ({ i }) => (
    <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between mb-1">
                <div className="font-sans text-sm text-fp-ink">{i.name}</div>
                <div className="font-mono text-xs text-fp-secondary">{i.realised} / {i.expected}</div>
            </div>
            <div className="h-1.5 bg-fp-border relative">
                <div className={`h-full ${i.tone === "success" ? "bg-fp-success" : i.tone === "warn" ? "bg-fp-saffron" : "bg-fp-terracotta"}`} style={{ width: `${i.pct}%` }} />
            </div>
        </div>
        <span className={`shrink-0 px-2 py-0.5 font-sans text-[10px] uppercase tracking-wider ${TONE_CLS[i.tone]}`}>{i.status}</span>
    </div>
);
